import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FiChevronRight, FiLogOut, FiPlus, FiSettings } from 'react-icons/fi';
import './VersionList.css';
import { FestivalData } from '../../model/FestivalData';
import { fetchCurrentUser, fetchVersions, imageUrl, updateFestivalData } from '../../services/api';
import { getImageUrl } from '../../services/imageService';
import BrandLogo from '../../components/BrandLogo';
import { PlaceholderItems, Skeleton } from '../../components/LoadingPlaceholder';
import SettingsDialog from '../../components/SettingsDialog';
import { currentMessages, useLanguage, useMessages } from '../../i18n/I18n';
import { isLanguage } from '../../i18n/language';
import { updateSessionUser } from '../../services/sessionCookie';

interface VersionListProps {
  sessionToken: string;
  festivalId: string;
  /** Only administrators can create a draft: the API refuses it to the others */
  canCreateDraft: boolean;
  onLogout: () => void;
}

const VersionList: React.FC<VersionListProps> = ({ sessionToken, festivalId, canCreateDraft, onLogout }) => {
  const [versions, setVersions] = useState<FestivalData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isNamingDraft, setIsNamingDraft] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const history = useHistory();
  const t = useMessages('versions');
  const common = useMessages('common');
  const { locale, setLanguage } = useLanguage();

  useEffect(() => {
    const loadVersions = async () => {
      try {
        setIsLoading(true);
        const fetchedVersions = await fetchVersions(sessionToken, festivalId);
        setVersions(fetchedVersions);
      } catch (err) {
        console.error('Error fetching versions:', err);
        setError(err instanceof Error ? err.message : currentMessages('versions').fetchError);
      } finally {
        setIsLoading(false);
      }
    };

    loadVersions();
  }, [sessionToken, festivalId]);

  // The language may have been changed on another device since this session was saved
  useEffect(() => {
    fetchCurrentUser(sessionToken, festivalId)
      .then(user => {
        if (!isLanguage(user.language)) return;
        setLanguage(user.language);
        updateSessionUser({ language: user.language });
      })
      .catch(error => console.error('Failed to refresh the account language:', error));
  }, [sessionToken, festivalId, setLanguage]);

  const latestVersion = versions.length > 0
    ? versions.reduce((latest, v) => (v.version > latest.version ? v : latest))
    : null;

  const handleNewDraftClick = () => {
    if (!latestVersion) {
      setError(t.noPreviousVersion);
      return;
    }
    setDraftName('');
    setIsNamingDraft(true);
  };

  const handleCreateNewDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!latestVersion) return;

    try {
      setIsCreatingDraft(true);
      setError(null);

      // Create new version data
      const newVersion: FestivalData = {
        ...latestVersion,
        version: latestVersion.version + 1,
        status: 'draft',
        internal_name: draftName.trim() || null,
        updated_at: new Date().toISOString()
      };

      // Save the new version to server
      await updateFestivalData(sessionToken, festivalId, newVersion.version.toString(), newVersion);
      
      // Navigate to editor
      history.push(`/editor/${newVersion.version}`);
    } catch (err) {
      console.error('Error creating new version:', err);
      setError(err instanceof Error ? err.message : t.createError);
      setIsNamingDraft(false);
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const liveVersion = versions
    .filter(version => version.status === 'published')
    .reduce<FestivalData | null>((live, version) => (!live || version.version > live.version ? version : live), null);
  const draftInProgress = latestVersion && latestVersion.status === 'draft' ? latestVersion : null;
  const festivalName = latestVersion?.infos.festival_name;
  const festivalLogo = latestVersion?.infos.festival_logo;

  const statusOf = (version: FestivalData) => {
    if (version.status === 'draft') return { className: 'draft', label: t.draft };
    if (version === liveVersion) return { className: 'live', label: t.liveInApps };
    return { className: 'superseded', label: t.superseded };
  };

  const editorLink = (version: FestivalData) => ({
    pathname: `/editor/${version.version}`,
    state: { versionData: version }
  });

  const renderVersionCard = (version: FestivalData, title: string, className: string) => (
    <Link to={editorLink(version)} className={`version-card ${className}`}>
      <span className="version-card-top">
        <StatusPill version={version} statusOf={statusOf} label={title} />
        <span className="version-card-date">{formatDate(version.updated_at, locale)}</span>
      </span>
      <span className="version-card-number">{t.version(version.version)}</span>
      <span className="version-card-name">{version.internal_name || version.infos.festival_name}</span>
      <span className="version-card-counts">
        <Count count={version.artists.length} label={t.artists} />
        <Count count={version.shows.length} label={t.shows} />
        <Count count={version.pois.filter(poi => poi.category === 'scene').length} label={t.stages} />
        <Count count={version.maps.length} label={t.maps} />
      </span>
    </Link>
  );

  return (
    <div className="version-list-page">
      <header className="version-list-header">
        <div className="version-list-brand">
          <BrandLogo size={32} />
          <span className="version-list-wordmark">Festival Systems</span>
          {festivalName && (
            <>
              <span className="version-list-divider" aria-hidden="true" />
              <span className="version-list-festival">
                {festivalLogo
                  ? <img src={getImageUrl(festivalLogo, identifier => imageUrl(identifier, festivalId))} alt="" />
                  : <span className="version-list-festival-initial" aria-hidden="true">{festivalName.charAt(0)}</span>}
                {festivalName}
              </span>
            </>
          )}
        </div>
        <div className="version-list-actions">
          <button type="button" onClick={() => setIsSettingsOpen(true)} className="version-list-settings">
            <FiSettings aria-hidden="true" />
            {t.settings}
          </button>
          <button type="button" onClick={onLogout} className="version-list-signout">
            <FiLogOut aria-hidden="true" />
            {t.signOut}
          </button>
        </div>
      </header>

      <main className="version-list-main">
        <div className="version-list-title">
          <div>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>
          {canCreateDraft && (
            <button
              type="button"
              onClick={handleNewDraftClick}
              className="new-draft-btn"
              disabled={isCreatingDraft}
            >
              <FiPlus aria-hidden="true" />
              {isCreatingDraft ? t.creatingDraft : t.newDraft}
            </button>
          )}
        </div>

        {error && <p className="error">{error}</p>}

        {isLoading ? (
          // The cards and table to come, so the versions appear where the placeholders were
          <div className="loading-placeholder version-list-placeholder" role="status" aria-busy="true">
            <span className="visually-hidden">{common.loading}</span>
            <div className="version-cards" aria-hidden="true">
              {[0, 1].map(index => (
                <div key={index} className="version-card">
                  <span className="version-card-top">
                    <Skeleton width={104} height={22} radius="var(--radius-pill)" />
                    <Skeleton width={128} height={13} />
                  </span>
                  <Skeleton width={72} height={13} />
                  <Skeleton width={index === 0 ? '58%' : '72%'} height={24} />
                  <Skeleton width="64%" height={13} />
                </div>
              ))}
            </div>
            <div className="loading-placeholder-table" aria-hidden="true">
              <PlaceholderItems layout="table" count={3} />
            </div>
          </div>
        ) : versions.length > 0 ? (
          <>
            {(liveVersion || draftInProgress) && (
              <div className="version-cards">
                {liveVersion && renderVersionCard(liveVersion, t.liveInApps, 'live')}
                {draftInProgress && renderVersionCard(draftInProgress, t.draftInProgress, 'draft')}
              </div>
            )}

            <section className="version-table" aria-labelledby="all-versions-title">
              <h2 id="all-versions-title">{t.allVersions}</h2>
              <div className="version-table-scroll">
                <div className="version-table-head" aria-hidden="true">
                  <span>{t.columns.version}</span>
                  <span>{t.columns.name}</span>
                  <span>{t.columns.status}</span>
                  <span>{t.columns.lastSaved}</span>
                  <span>{t.columns.content}</span>
                </div>
                <ul className="version-list">
                  {versions.map((version) => (
                    <li key={version.version}>
                      <Link to={editorLink(version)} className="version-link" aria-label={t.openVersion(version.version)}>
                        <span className="version-number">{version.version}</span>
                        <span className={`version-name${version.internal_name ? '' : ' fallback'}`}>
                          {version.internal_name || version.infos.festival_name}
                        </span>
                        <span><StatusPill version={version} statusOf={statusOf} /></span>
                        <span className="version-date">{formatDate(version.updated_at, locale)}</span>
                        <span className="version-counts">{t.rowCounts(version.artists.length, version.shows.length)}</span>
                        <FiChevronRight className="version-chevron" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </>
        ) : (
          <p className="version-list-empty">{t.empty}</p>
        )}
      </main>
      {isNamingDraft && latestVersion && (
        <div className="new-draft-modal-overlay" onClick={() => !isCreatingDraft && setIsNamingDraft(false)}>
          <form className="new-draft-modal" onClick={e => e.stopPropagation()} onSubmit={handleCreateNewDraft}>
            <h2>{t.newDraftTitle}</h2>
            <label htmlFor="new_draft_internal_name">{t.internalName}</label>
            <input
              id="new_draft_internal_name"
              type="text"
              value={draftName}
              placeholder={latestVersion.infos.festival_name}
              onChange={e => setDraftName(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && !isCreatingDraft && setIsNamingDraft(false)}
              disabled={isCreatingDraft}
              autoFocus
            />
            <p>{t.newDraftHint(latestVersion.version, latestVersion.version + 1)}</p>
            <div className="new-draft-modal-buttons">
              <button type="button" className="cancel" onClick={() => setIsNamingDraft(false)} disabled={isCreatingDraft}>
                {common.cancel}
              </button>
              <button type="submit" className="create" disabled={isCreatingDraft}>
                {isCreatingDraft ? t.creating : t.createDraft}
              </button>
            </div>
          </form>
        </div>
      )}
      {isSettingsOpen && (
        <SettingsDialog sessionToken={sessionToken} festivalId={festivalId} onClose={() => setIsSettingsOpen(false)} />
      )}
    </div>
  );
};

const formatDate = (isoDate: string | null, locale: string) => isoDate
  ? new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    .format(new Date(isoDate))
  : '';

/** A count in bold, then what it counts */
const Count: React.FC<{ count: number; label: (count: number) => string }> = ({ count, label }) => (
  <span><strong>{count}</strong> {label(count)}</span>
);

interface StatusPillProps {
  version: FestivalData;
  statusOf: (version: FestivalData) => { className: string; label: string };
  label?: string;
}

const StatusPill: React.FC<StatusPillProps> = ({ version, statusOf, label }) => {
  const status = statusOf(version);
  return <span className={`status-pill ${status.className}`}>{label ?? status.label}</span>;
};

export default VersionList;
